/* ═══════════════════════════════════════════════════════════════════
   BLISS · DIVINE ENERGY — Breath sound engine.
   Custom pattern: 15s air (smooth ramp low→high→low→void) + 15s pure void.
   Void time = user visualizes their dream, eyes closed.
   Pure Web Audio API — zero cloud cost.
   ─────────────────────────────────────────────────────────────────── */
import { getAudioCtx } from './audioCore';


let _ctx = null;
let _activeSession = null;

const getCtx = () => {
  if (typeof window === 'undefined') return null;
  _ctx = getAudioCtx();
  return _ctx;
};

/** Build a long pink-noise buffer once — reused for every air segment. */
const buildNoiseBuffer = (ctx, seconds = 20) => {
  const buf = ctx.createBuffer(1, ctx.sampleRate * seconds, ctx.sampleRate);
  const data = buf.getChannelData(0);
  // Pink-ish noise via IIR filter for a soft "wind through cave" feel
  let b0 = 0, b1 = 0, b2 = 0, b3 = 0, b4 = 0, b5 = 0, b6 = 0;
  for (let i = 0; i < data.length; i++) {
    const white = Math.random() * 2 - 1;
    b0 = 0.99886 * b0 + white * 0.0555179;
    b1 = 0.99332 * b1 + white * 0.0750759;
    b2 = 0.96900 * b2 + white * 0.1538520;
    b3 = 0.86650 * b3 + white * 0.3104856;
    b4 = 0.55000 * b4 + white * 0.5329522;
    b5 = -0.7616 * b5 - white * 0.0168980;
    data[i] = (b0 + b1 + b2 + b3 + b4 + b5 + b6 + white * 0.5362) * 0.11;
    b6 = white * 0.115926;
  }
  return buf;
};

/** Singing bowl chime — start/end/phase-transition marker (borrowed pattern
    from peaceSounds).  Layered sine partials, ~3.5s decay. */
export const startChime = () => {
  const ctx = getCtx();
  if (!ctx) return;
  const partials = [
    { f: 440, gain: 0.20, decay: 3.4 },
    { f: 660, gain: 0.13, decay: 3.0, delay: 0.03 },
    { f: 880, gain: 0.07, decay: 2.4, delay: 0.05 },
  ];
  partials.forEach(({ f, gain, decay, delay = 0 }) => {
    const t = ctx.currentTime + delay;
    const osc = ctx.createOscillator();
    const g = ctx.createGain();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(f, t);
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(gain, t + 0.08);
    g.gain.exponentialRampToValueAtTime(0.0001, t + decay);
    osc.connect(g).connect(ctx.destination);
    osc.start(t); osc.stop(t + decay + 0.2);
  });
};

export const endChime = () => {
  startChime();
  setTimeout(() => startChime(), 1100);
};

/**
 * Schedule ONE "air" segment — smoothly ramps volume up from silence to peak,
 * then back down to silence over the given duration.  Uses a filtered noise
 * source (breath-like) with a low-pass filter for warmth.
 *
 * curve profile (duration = D seconds):
 *   0     → gain 0     (silence)
 *   D*0.4 → gain peak  (crescendo)
 *   D*0.6 → gain peak  (sustain)
 *   D     → gain 0     (dwindle to void)
 *
 * @param {AudioContext} ctx
 * @param {AudioBuffer}  noiseBuf
 * @param {number}       startAt  — absolute ctx.currentTime + offset
 * @param {number}       dur      — seconds
 * @param {number}       peak     — peak gain (0-1)
 */
const scheduleAirSegment = (ctx, noiseBuf, startAt, dur, peak = 0.28) => {
  const src = ctx.createBufferSource();
  src.buffer = noiseBuf;
  const filt = ctx.createBiquadFilter();
  filt.type = 'lowpass';
  filt.frequency.setValueAtTime(700, startAt);
  filt.frequency.linearRampToValueAtTime(1200, startAt + dur * 0.4);
  filt.frequency.linearRampToValueAtTime(700, startAt + dur);
  filt.Q.value = 0.9;
  const g = ctx.createGain();
  g.gain.setValueAtTime(0.0001, startAt);
  g.gain.linearRampToValueAtTime(peak, startAt + dur * 0.4);
  g.gain.setValueAtTime(peak, startAt + dur * 0.6);
  g.gain.linearRampToValueAtTime(0.0001, startAt + dur);
  src.connect(filt).connect(g).connect(ctx.destination);
  src.start(startAt);
  src.stop(startAt + dur + 0.05);
  return src;
};

/**
 * Start a divine breath session.
 *
 * @param {object} opts
 * @param {number} opts.airSec        — length of each air segment (default 15)
 * @param {number} opts.voidSec       — length of each void segment (default 15)
 * @param {number} opts.totalCycles   — total air+void cycles (10 min / 30s = 20)
 * @param {(state:{ phase:'air'|'void', cycleIndex:number, secondsIntoPhase:number }) => void} opts.onTick
 * @param {() => void} opts.onComplete
 * @returns {{ stop: () => void }}
 */
export const startBreathSession = ({
  airSec = 15,
  voidSec = 15,
  totalCycles = 20,
  onTick = () => {},
  onComplete = () => {},
} = {}) => {
  stopBreathSession(); // ensure only one active
  const ctx = getCtx();
  if (!ctx) return { stop: () => {} };
  try { ctx.resume?.(); } catch { /* noop */ }

  const noiseBuf = buildNoiseBuffer(ctx, airSec + 2);
  const sources = [];
  const cycleSec = airSec + voidSec;
  const totalSec = totalCycles * cycleSec;
  const t0 = ctx.currentTime + 0.15;

  // Pre-schedule ALL air segments up front (browser handles them precisely).
  for (let i = 0; i < totalCycles; i++) {
    const airStart = t0 + i * cycleSec;
    sources.push(scheduleAirSegment(ctx, noiseBuf, airStart, airSec));
  }

  // Tick loop (1Hz) to drive UI phase indicator + onComplete
  // UI clock follows the AUDIO clock (minus output latency) so visuals match what is heard.
  const heardElapsed = () => Math.max(0, ctx.currentTime - (ctx.outputLatency || ctx.baseLatency || 0) - t0);
  const tickIv = setInterval(() => {
    const elapsed = heardElapsed();
    if (elapsed >= totalSec) {
      onTick({ phase: 'void', cycleIndex: totalCycles - 1, secondsIntoPhase: voidSec, done: true });
      stopBreathSession();
      onComplete();
      return;
    }
    const cycleIndex = Math.floor(elapsed / cycleSec);
    const intoCycle = elapsed - cycleIndex * cycleSec;
    const phase = intoCycle < airSec ? 'air' : 'void';
    const secondsIntoPhase = phase === 'air' ? intoCycle : intoCycle - airSec;
    onTick({ phase, cycleIndex, secondsIntoPhase, done: false });
  }, 250);

  _activeSession = {
    sources, tickIv,
    stop() {
      clearInterval(tickIv);
      sources.forEach(s => { try { s.stop(); } catch { /* noop */ } });
    },
  };

  return { stop: stopBreathSession };
};

export const stopBreathSession = () => {
  if (_activeSession) {
    try { _activeSession.stop(); } catch { /* noop */ }
    _activeSession = null;
  }
};

/** Spoken cue (used for "Close your eyes now" opening line). */
export const speak = (text, { rate = 0.72, pitch = 0.9, volume = 0.85 } = {}) => {
  if (typeof window === 'undefined' || !window.speechSynthesis) return;
  setTimeout(() => {          // off the UI frame — Android TTS can block
    try {
      const ss = window.speechSynthesis;
      if (ss.speaking || ss.pending) ss.cancel();
      const u = new SpeechSynthesisUtterance(text);
      u.rate = rate; u.pitch = pitch; u.volume = volume;
      ss.speak(u);
    } catch { /* silent */ }
  }, 0);
};

export const stopSpeech = () => {
  try { window.speechSynthesis?.cancel(); } catch { /* noop */ }
};
